import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Breadcrumbs,
  Button,
  Stack,
  Tab,
  Tabs,
  Typography,
  Box,
  FormControl,
  TextField,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { startOfWeek, addDays, format, parseISO } from "date-fns";
import { sk } from "date-fns/locale";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import { useJumboTheme } from "@jumbo/hooks";
import Div from "@jumbo/shared/Div";
import DragIndicator from "@mui/icons-material/DragIndicator";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { DAYS, DAILY_MENU_TYPES } from "app/utils/constants/settings";
import { getStoredMenu, saveMenu } from "app/data/dailyMenuAdmin.storage";
import {
  createDailyMenu,
  getDailyMenuById,
  updateDailyMenuById,
  getDailyMenuSettings,
  mailchimpSend,
  mailchimpTestEmail,
} from "app/services/api/dailyMenu";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { getDSOKProducts } from "app/services/api/management";
import DsokDailyMenuProductRow from "./DsokDailyMenuProductRow";
import { downloadDailyMenuCsv } from "./dsokDailyMenuCsvExport";
import {
  LunchMenuPrintFrame,
  triggerDailyMenuPrint,
} from "./print/LunchMenuPrintFrame";
import {
  buildWeekFromMenu,
  sheetTitleForMenuType,
  DEFAULT_PRINT_COPY_SK,
} from "./print/dsokMenuToPrint";
import { downloadDailyMenuDocx } from "./print/dsokMenuToDocx";

/** Normalizuje názov produktu na objekt { "sk-SK", "en-US" } pre menu. */
function normalizeProductTitle(productNameOrTitle) {
  if (!productNameOrTitle) return {};
  if (typeof productNameOrTitle === "string")
    return { "sk-SK": productNameOrTitle, "en-US": productNameOrTitle };
  if (
    typeof productNameOrTitle === "object" &&
    !Array.isArray(productNameOrTitle)
  )
    return { ...productNameOrTitle };
  return {};
}

/** Vyextrahuje URL z hodnoty (string alebo objekt s url/src). */
function extractImageUrl(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null) {
    const u = value?.url ?? value?.src ?? value?.imageUrl ?? value?.link;
    if (typeof u === "string") return u;
  }
  return "";
}

/** Vráti URL obrázka produktu z DSOK (rôzne názvy polí v API). */
function getProductImageUrl(p) {
  const candidates = [
    p?.imageUrl,
    p?.image,
    p?.customProductImageUrl,
    p?.mainImage,
    p?.productImage,
    p?.metadata?.image,
    Array.isArray(p?.images) && p.images[0] ? p.images[0] : null,
  ];
  for (const v of candidates) {
    const url = extractImageUrl(v);
    if (url) return url;
  }
  return "";
}

/** Váha produktu v kg z nastavení (productParameters.weightKg). null ak nie je. */
function getProductWeightKg(p) {
  const raw =
    p?.productParameters?.weightKg ??
    p?.weightKg ??
    p?.productParameters?.weight ??
    null;
  if (raw === null || raw === undefined || raw === "") return null;
  const num = typeof raw === "number" ? raw : parseFloat(raw);
  return Number.isFinite(num) ? num : null;
}

/** Objem produktu v litroch z nastavení (productParameters.volumeLiters). null ak nie je. */
function getProductVolumeLiters(p) {
  const raw = p?.productParameters?.volumeLiters ?? p?.volumeLiters ?? null;
  if (raw === null || raw === undefined || raw === "") return null;
  const num = typeof raw === "number" ? raw : parseFloat(raw);
  return Number.isFinite(num) ? num : null;
}

/**
 * Normalizuje alergény z produktu na pole ČÍSEL. Produkt ich môže mať v rôznych
 * tvaroch: pole čísel / reťazcov / objektov { number }, alebo JSON-string / CSV.
 * Vráti unikátne zoradené čísla.
 */
function normalizeAllergens(raw) {
  let arr = raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      arr = JSON.parse(raw);
    } catch (e) {
      arr = raw.split(/[,;\s]+/);
    }
  }
  if (!Array.isArray(arr)) return [];
  const nums = arr
    .map((item) => {
      if (typeof item === "number") return item;
      if (typeof item === "string") return Number(item);
      if (item && typeof item === "object")
        return Number(item.number ?? item.id ?? item.value ?? item.code);
      return NaN;
    })
    .filter((n) => Number.isFinite(n));
  return [...new Set(nums)].sort((a, b) => a - b);
}

/** Normalizuje DSOK produkt do formátu menu (productsById). */
function dsokProductToMenuProduct(p) {
  const id = p?._id ?? p?.id;
  if (!id) return null;
  let title = normalizeProductTitle(p?.productName);
  if (!Object.keys(title).length) title = normalizeProductTitle(p?.title);
  if (!Object.keys(title).length)
    title = { "sk-SK": String(id), "en-US": String(id) };
  return {
    id,
    title,
    // Názov pre web — default rovnaký ako názov jedla z produktov.
    webTitle: { ...title },
    // Zobraziť na webe — default true.
    showOnWeb: true,
    // Váha (kg) / objem (l) z nastavení produktu — snapshot pre web export + zobrazenie.
    weightKg: getProductWeightKg(p),
    volumeLiters: getProductVolumeLiters(p),
    description: p?.description ?? p?.productDescription ?? {},
    price: p?.price ?? p?.salePrice ?? "",
    isActive: p?.isActive !== false,
    isGlutenFree: p?.isGlutenFree === true || p?.isGlutenFree === "true",
    imageUrl: getProductImageUrl(p),
    allergens: normalizeAllergens(p?.allergens),
  };
}

/** Normalizuje hodnotu isActive/active z backendu (boolean alebo string "true"/"false") na boolean. */
function normalizeIsActive(product) {
  if (!product || typeof product !== "object") return true;
  const v = product.isActive ?? product.active;
  if (v === false || v === "false") return false;
  if (v === true || v === "true") return true;
  return true;
}

/** Normalizuje menu načítané z API: productsById dostanú konzistentné isActive (z isActive/active, string "true"/"false"). */
function normalizeMenuFromApi(menu) {
  if (!menu || typeof menu !== "object") return menu;
  const next = { ...menu };
  if (next.productsById && typeof next.productsById === "object") {
    const productsById = { ...next.productsById };
    Object.keys(productsById).forEach((id) => {
      const p = productsById[id];
      if (p && typeof p === "object") {
        productsById[id] = {
          ...p,
          isActive: normalizeIsActive(p),
        };
      }
    });
    next.productsById = productsById;
  }
  return next;
}

/** Z odpovede getDSOKProducts (object alebo array) vráti pole produktov s id. */
function normalizeDsokProductsList(products) {
  if (!products) return [];
  const arr = Array.isArray(products)
    ? products
    : Object.keys(products).map((k) => ({
        ...products[k],
        _id: products[k]?._id ?? k,
      }));
  return arr.map((p) => ({ ...p, id: p._id ?? p.id }));
}

/** Vráti zobrazovaný názov produktu pre Autocomplete a zoznam (title/productName, viac jazykov). */
function getOptionDisplayName(opt, lang) {
  if (!opt) return "";
  const name =
    opt?.title?.[lang] ||
    opt?.productName?.[lang] ||
    opt?.title?.["sk-SK"] ||
    opt?.productName?.["sk-SK"] ||
    opt?.title?.["en-US"] ||
    opt?.productName?.["en-US"] ||
    opt?.title?.["sk"] ||
    opt?.productName?.["sk"] ||
    opt?.title?.["en"] ||
    opt?.productName?.["en"];
  if (typeof name === "string" && name) return name;
  const obj = opt?.productName ?? opt?.title;
  if (obj && typeof obj === "object") {
    const v = Object.values(obj).find((x) => typeof x === "string" && x);
    if (v) return v;
  }
  return opt?.name || opt?.id || opt?._id || "";
}

/** Riadok produktu s drag handle pre zmenu poradia. */
function SortableProductRow({
  product,
  number,
  lang,
  onToggleActive,
  onDelete,
  onUpdate,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "stretch",
        gap: 0,
        mb: 1,
        "& .sortable-card": { flex: 1, minWidth: 0 },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: "flex",
          alignItems: "center",
          px: 0.5,
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <DragIndicator sx={{ color: "text.secondary", fontSize: 22 }} />
      </Box>
      <Box className="sortable-card">
        <DsokDailyMenuProductRow
          product={product}
          number={number}
          lang={lang}
          onToggleActive={onToggleActive}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      </Box>
    </Box>
  );
}

/** Vráti pondelok týždňa, v ktorom je daný dátum. */
function getMonday(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : parseISO(date);
  return startOfWeek(d, { weekStartsOn: 1 });
}

/** Z pondelka vygeneruje dátumy pre všetky dni (monday..sunday). */
function getDatesForWeek(mondayDate) {
  if (!mondayDate) return {};
  const monday = mondayDate instanceof Date ? mondayDate : parseISO(mondayDate);
  const out = {};
  DAYS.forEach((day, i) => {
    out[day] = format(addDays(monday, i), "yyyy-MM-dd");
  });
  return out;
}

const DsokDailyMenuEdit = () => {
  const { dailyMenuId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { theme } = useJumboTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { getAuthUser } = useJumboAuth();
  const lang = i18n.language?.startsWith("sk") ? "sk-SK" : "en-US";
  const customerCode =
    getAuthUser()?.customer?.code ??
    getAuthUser()?.customer?.customerCode ??
    "";

  const [menu, setMenu] = useState(null);
  /** Id záznamu na backende (null = nové/skopírované menu, ešte neuložené). */
  const [idZaznamu, setIdZaznamu] = useState(null);
  const [selectedDay, setSelectedDay] = useState("monday");
  const [updating, setUpdating] = useState(false);
  const [dsokProducts, setDsokProducts] = useState([]);
  const [weekStartDateError, setWeekStartDateError] = useState(null);
  /** Chyba z posledného pokusu o uloženie (update/create). { message, code?, status? } */
  const [saveError, setSaveError] = useState(null);
  const [menuTypesConfig, setMenuTypesConfig] = useState(null);
  // CSV export nastavenia z obrazovkových nastavení denného menu (screen-settings → Export → CSV).
  const [csvExportSettings, setCsvExportSettings] = useState(null);
  // Logo zákazníka z nastavení denného menu (pre PDF/tlač lístka / Word).
  const [menuLogo, setMenuLogo] = useState("");
  const [docxExporting, setDocxExporting] = useState(false);
  // Mailchimp — odoslanie týždenného menu.
  const [mcEnabled, setMcEnabled] = useState(false);
  const [mcSending, setMcSending] = useState(false);
  const [mcConfirmOpen, setMcConfirmOpen] = useState(false);
  const [mcTestOpen, setMcTestOpen] = useState(false);
  const [mcTestEmailAddr, setMcTestEmailAddr] = useState("");
  const [mcTestSending, setMcTestSending] = useState(false);
  const [soupAutocompleteInput, setSoupAutocompleteInput] = useState("");
  const [mealAutocompleteInput, setMealAutocompleteInput] = useState("");
  const [soupAutocompleteKey, setSoupAutocompleteKey] = useState(0);
  const [mealAutocompleteKey, setMealAutocompleteKey] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    let cancelled = false;
    getDailyMenuSettings()
      .then((res) => {
        if (cancelled) return;
        const settings = res?.settings ?? res;
        setMenuTypesConfig(
          settings && typeof settings.menuTypes === "object"
            ? settings.menuTypes
            : {},
        );
        setCsvExportSettings(
          settings && typeof settings.csvExport === "object"
            ? settings.csvExport
            : {},
        );
        setMenuLogo(typeof settings?.logo === "string" ? settings.logo : "");
        setMcEnabled(
          settings?.integrations?.mailchimp?.enabled === true,
        );
      })
      .catch(() => {
        if (!cancelled) {
          setMenuTypesConfig({});
          setCsvExportSettings({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Typy menu ponúkané v selecte: len aktívne (podľa nastavení obrazovky).
  // Aktuálne nastavený typ ponecháme, aby sa existujúca hodnota nestratila.
  const availableMenuTypes = useMemo(() => {
    const mt = menuTypesConfig;
    const current = menu?.menuType;
    return DAILY_MENU_TYPES.filter((typ) => {
      if (typ.code === current) return true;
      if (!mt || typeof mt !== "object") return true;
      return mt[typ.code]?.active !== false;
    });
  }, [menuTypesConfig, menu?.menuType]);

  useEffect(() => {
    if (dailyMenuId === "new" || !dailyMenuId) {
      navigate("/customer/dsok/daily-menu", { replace: true });
      return;
    }
    let cancelled = false;
    const loadMenu = async () => {
      try {
        const raw = await getDailyMenuById(dailyMenuId);
        if (cancelled) return;
        const record = raw?.dailyMenu ?? raw?.data ?? raw;
        if (!record) {
          // fallback to storage below
        } else if (record?.menus?.length) {
          const firstMenu = record.menus[0];
          const menuForForm = normalizeMenuFromApi({
            ...JSON.parse(JSON.stringify(firstMenu)),
            id: record._id ?? dailyMenuId,
          });
          setIdZaznamu(record._id ?? null);
          setMenu(menuForForm);
          return;
        } else if (
          record &&
          (record.days != null || record.productsById != null)
        ) {
          const menuForForm = normalizeMenuFromApi({
            ...JSON.parse(JSON.stringify(record)),
            id: record._id ?? record.id ?? dailyMenuId,
          });
          setIdZaznamu(record._id ?? record.id ?? null);
          setMenu(menuForForm);
          return;
        }
      } catch (_) {
        if (cancelled) return;
      }
      const data = getStoredMenu(dailyMenuId);
      if (!data) {
        navigate("/customer/dsok/daily-menu", { replace: true });
        return;
      }
      const menuCopy = JSON.parse(JSON.stringify(data));
      if (menuCopy.weekStartDate && menuCopy.days) {
        const dates = getDatesForWeek(menuCopy.weekStartDate);
        DAYS.forEach((day) => {
          if (menuCopy.days[day]) {
            menuCopy.days[day].date = dates[day] ?? menuCopy.days[day].date;
          }
        });
      }
      setIdZaznamu(null);
      setMenu(menuCopy);
    };
    loadMenu();
    return () => {
      cancelled = true;
    };
  }, [dailyMenuId, navigate]);

  useEffect(() => {
    let cancelled = false;
    getDSOKProducts()
      .then((products) => {
        if (!cancelled) setDsokProducts(normalizeDsokProductsList(products));
      })
      .catch(() => {
        if (!cancelled) setDsokProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const getMenuDisplayName = (m) => {
    if (!m?.name) return m?.id || "";
    if (typeof m.name === "string") return m.name;
    return m.name["sk-SK"] || m.name["en-US"] || m.id;
  };

  const soupOptions = dsokProducts;
  const mealOptions = dsokProducts;

  const weekStartDate = menu?.weekStartDate
    ? menu.weekStartDate instanceof Date
      ? menu.weekStartDate
      : parseISO(menu.weekStartDate)
    : null;
  const datesForWeek = useMemo(
    () => getDatesForWeek(menu?.weekStartDate),
    [menu?.weekStartDate],
  );

  const handleWeekStartDateChange = (newValue) => {
    if (!menu) return;
    setWeekStartDateError(null);
    const monday = getMonday(newValue);
    const iso = monday ? format(monday, "yyyy-MM-dd") : null;
    if (!iso) {
      setMenu((prev) => ({ ...prev, weekStartDate: undefined }));
      return;
    }
    const dates = getDatesForWeek(iso);
    setMenu((prev) => {
      const nextDays = {};
      DAYS.forEach((day) => {
        nextDays[day] = {
          ...(prev.days?.[day] || {}),
          date: dates[day],
          soups: prev.days?.[day]?.soups ?? [],
          meals: prev.days?.[day]?.meals ?? [],
          ticker: prev.days?.[day]?.ticker,
          updatedAt: new Date().toISOString(),
        };
      });
      return { ...prev, weekStartDate: iso, days: nextDays };
    });
  };

  const handleReorderSoups = (activeId, overId) => {
    if (!menu || activeId === overId) return;
    const ids = [...soupIds];
    const oldIndex = ids.indexOf(activeId);
    const newIndex = ids.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(ids, oldIndex, newIndex);
    setMenu((prev) => ({
      ...prev,
      days: {
        ...prev.days,
        [selectedDay]: {
          ...prev.days[selectedDay],
          soups: reordered,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
  };

  const handleReorderMeals = (activeId, overId) => {
    if (!menu || activeId === overId) return;
    const ids = [...mealIds];
    const oldIndex = ids.indexOf(activeId);
    const newIndex = ids.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(ids, oldIndex, newIndex);
    setMenu((prev) => ({
      ...prev,
      days: {
        ...prev.days,
        [selectedDay]: {
          ...prev.days[selectedDay],
          meals: reordered,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
  };

  // Mapa produktov z katalógu podľa id (na dopĺňanie váhy pre staršie menu bez uloženej weightKg).
  const dsokProductById = useMemo(() => {
    const map = {};
    (dsokProducts || []).forEach((p) => {
      const id = p?.id ?? p?._id;
      if (id != null) map[String(id)] = p;
    });
    return map;
  }, [dsokProducts]);

  // Doplní weightKg / volumeLiters z katalógu, ak menu produkt tieto hodnoty nemá (staršie menu).
  const enrichRowProduct = (prod) => {
    if (!prod) return prod;
    const needsWeight = prod.weightKg == null;
    const needsVolume = prod.volumeLiters == null;
    if (!needsWeight && !needsVolume) return prod;
    const cat = dsokProductById[String(prod.id)];
    if (!cat) return prod;
    const next = { ...prod };
    if (needsWeight) {
      const w = getProductWeightKg(cat);
      if (w != null) next.weightKg = w;
    }
    if (needsVolume) {
      const v = getProductVolumeLiters(cat);
      if (v != null) next.volumeLiters = v;
    }
    return next;
  };

  const dayData = menu?.days?.[selectedDay];
  const soupIds = dayData?.soups || [];
  const mealIds = dayData?.meals || [];
  const soupsResolved = soupIds
    .map((id) =>
      menu?.productsById?.[id]
        ? enrichRowProduct({ ...menu.productsById[id], id })
        : null,
    )
    .filter(Boolean);
  const mealsResolved = mealIds
    .map((id) =>
      menu?.productsById?.[id]
        ? enrichRowProduct({ ...menu.productsById[id], id })
        : null,
    )
    .filter(Boolean);

  const handleAddProduct = (productOrId, type) => {
    if (!menu) return;
    const product = typeof productOrId === "object" ? productOrId : null;
    const productId = product ? (product.id ?? product._id) : productOrId;
    if (!productId) return;
    const ids = type === "soup" ? [...soupIds] : [...mealIds];
    if (ids.includes(productId)) {
      enqueueSnackbar(t("pages.menus.productIsAlreadyAdded"), {
        variant: "warning",
      });
      return;
    }
    ids.push(productId);
    const menuProduct = product ? dsokProductToMenuProduct(product) : null;
    setMenu((prev) => {
      const nextProductsById = { ...prev.productsById };
      if (menuProduct && !nextProductsById[productId]) {
        nextProductsById[productId] = menuProduct;
      }
      return {
        ...prev,
        productsById: nextProductsById,
        days: {
          ...prev.days,
          [selectedDay]: {
            ...prev.days[selectedDay],
            [type === "soup" ? "soups" : "meals"]: ids,
            updatedAt: new Date().toISOString(),
          },
        },
      };
    });
  };

  const handleRemoveProduct = (productId, type) => {
    if (!menu) return;
    const key = type === "soup" ? "soups" : "meals";
    const ids = (menu.days[selectedDay]?.[key] || []).filter(
      (id) => id !== productId,
    );
    setMenu((prev) => ({
      ...prev,
      days: {
        ...prev.days,
        [selectedDay]: {
          ...prev.days[selectedDay],
          [key]: ids,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
  };

  const handleToggleActive = (productId, checked) => {
    if (!menu?.productsById?.[productId]) return;
    setMenu((prev) => ({
      ...prev,
      productsById: {
        ...prev.productsById,
        [productId]: { ...prev.productsById[productId], isActive: checked },
      },
    }));
  };

  const handleUpdateProduct = (productId, field, value) => {
    if (!menu?.productsById?.[productId]) return;
    setMenu((prev) => ({
      ...prev,
      productsById: {
        ...prev.productsById,
        [productId]: { ...prev.productsById[productId], [field]: value },
      },
    }));
  };

  const handleSave = async () => {
    if (!menu) return;
    if (!menu.weekStartDate) {
      const msg = t("pages.dsokDailyMenu.weekStartDateRequired", {
        defaultValue: "Dátum začiatku týždňa je povinný.",
      });
      setWeekStartDateError(msg);
      enqueueSnackbar(msg, { variant: "warning" });
      return;
    }
    setWeekStartDateError(null);
    setSaveError(null);
    setUpdating(true);
    try {
      const dates = getDatesForWeek(menu.weekStartDate);
      const daysWithDates = {};
      DAYS.forEach((day) => {
        daysWithDates[day] = {
          ...(menu.days?.[day] || {}),
          date: dates[day],
          soups: menu.days?.[day]?.soups ?? [],
          meals: menu.days?.[day]?.meals ?? [],
        };
      });
      // Normalizuj productsById: dopíš weightKg (z katalógu, kg), webTitle (default = názov)
      // a showOnWeb (default true), aby ich mal aj web export k dispozícii.
      const normalizedProductsById = {};
      Object.entries(menu.productsById || {}).forEach(([id, p]) => {
        if (!p || typeof p !== "object") {
          normalizedProductsById[id] = p;
          return;
        }
        const cat = dsokProductById[String(id)];
        // Pri uložení natiahni SPRÁVNE hodnoty z katalógu produktov (alergény, váha,
        // objem). Katalóg má prednosť; ak hodnotu nemá, ponechá sa doterajší snapshot.
        const catWeight = cat ? getProductWeightKg(cat) : null;
        const weightKg =
          catWeight != null ? catWeight : p.weightKg != null ? p.weightKg : null;
        const catVolume = cat ? getProductVolumeLiters(cat) : null;
        const volumeLiters =
          catVolume != null
            ? catVolume
            : p.volumeLiters != null
              ? p.volumeLiters
              : null;
        // Alergény: katalóg má prednosť (ak má pole vyplnené), inak snapshot z menu.
        // Robustne normalizuj rôzne tvary (čísla/reťazce/objekty/JSON) na čísla.
        const catAllergens =
          cat && cat.allergens !== undefined
            ? normalizeAllergens(cat.allergens)
            : null;
        const allergens =
          catAllergens && catAllergens.length
            ? catAllergens
            : normalizeAllergens(p.allergens);
        const webTitle =
          p.webTitle &&
          typeof p.webTitle === "object" &&
          Object.keys(p.webTitle).length > 0
            ? p.webTitle
            : { ...(p.title || {}) };
        const showOnWeb = p.showOnWeb === false ? false : true;
        normalizedProductsById[id] = {
          ...p,
          weightKg,
          volumeLiters,
          allergens,
          webTitle,
          showOnWeb,
        };
      });

      const { customerId, ...menuWithoutCustomerId } = menu;
      const menuToSend = {
        ...menuWithoutCustomerId,
        productsById: normalizedProductsById,
        days: daysWithDates,
        customerCode: menu?.customerCode ?? customerCode,
      };
      let result;
      if (idZaznamu) {
        result = await updateDailyMenuById(idZaznamu, menuToSend);
        const savedMenu = { ...menuToSend, id: idZaznamu };
        setMenu(savedMenu);
        saveMenu(savedMenu);
      } else {
        result = await createDailyMenu(menuToSend);
        const newId = result?.id ?? result?._id;
        if (newId) {
          const savedMenu = { ...menuToSend, id: newId };
          setMenu(savedMenu);
          saveMenu(savedMenu);
          setIdZaznamu(newId);
          navigate(`/customer/dsok/daily-menu/${newId}`, { replace: true });
        } else {
          saveMenu(menuToSend);
        }
      }
      enqueueSnackbar(
        t("notifications.wasSuccessful", {
          defaultValue: "Saved successfully",
        }),
        {
          variant: "success",
        },
      );
    } catch (e) {
      const body = e?.response?.data;
      const message =
        (body && typeof body === "object" && body.message) ||
        (typeof body === "string" ? body : null) ||
        e?.message ||
        t("notifications.wasFailed", { defaultValue: "Uloženie zlyhalo." });
      const code =
        (body && typeof body === "object" && body.code) != null
          ? body.code
          : e?.response?.status;
      const status = body && typeof body === "object" ? body.status : null;
      setSaveError({ message, code, status });
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setUpdating(false);
    }
  };

  const handleExportCsv = () => {
    if (!menu) return;
    try {
      const filename = downloadDailyMenuCsv(menu, {
        // Obohatíme produkt o váhu/objem z katalógu (staršie menu ich nemá uložené) — rovnako ako v riadku.
        resolveProduct: (id) => {
          const p = menu?.productsById?.[id];
          return p ? enrichRowProduct({ ...p, id }) : null;
        },
        // Nastavenia z screen-settings (Export → CSV): ktoré stĺpce a zloženie názvu jedla.
        options: csvExportSettings,
      });
      enqueueSnackbar(
        t("pages.dsokDailyMenu.csvExported", {
          defaultValue: "CSV exportované: {{file}}",
          file: filename,
        }),
        { variant: "success" },
      );
    } catch (e) {
      enqueueSnackbar(
        t("pages.dsokDailyMenu.csvExportFailed", {
          defaultValue: "Export CSV zlyhal.",
        }),
        { variant: "error" },
      );
    }
  };

  const handleExportDocx = async () => {
    if (!menu) return;
    setDocxExporting(true);
    try {
      const week = buildWeekFromMenu(menu, {
        lang,
        resolveProduct: (id) => {
          const p = menu?.productsById?.[id];
          return p ? enrichRowProduct({ ...p, id }) : null;
        },
      });
      await downloadDailyMenuDocx({
        days: week.days,
        menuType: menu?.menuType,
        sheetTitle: sheetTitleForMenuType(menu?.menuType),
        brandName: getAuthUser()?.customer?.name || "",
        logoUrl: menuLogo,
        copy: DEFAULT_PRINT_COPY_SK,
        filename: `jedalny-listok_${menu?.menuType || "denne"}.docx`,
      });
      enqueueSnackbar(
        t("pages.dsokDailyMenu.docxExported", {
          defaultValue: "Word (.docx) vygenerovaný.",
        }),
        { variant: "success" },
      );
    } catch (e) {
      enqueueSnackbar(
        t("pages.dsokDailyMenu.docxExportFailed", {
          defaultValue: "Export Word zlyhal.",
        }),
        { variant: "error" },
      );
    } finally {
      setDocxExporting(false);
    }
  };

  // Podklady pre Mailchimp e-mail — názvy z „Názov pre web" (preferWeb), celý týždeň.
  const buildMailchimpPayload = () => {
    const week = buildWeekFromMenu(menu, {
      lang,
      preferWeb: true,
      resolveProduct: (id) => {
        const p = menu?.productsById?.[id];
        return p ? enrichRowProduct({ ...p, id }) : null;
      },
    });
    return {
      days: week.days,
      week: week.week,
      title: sheetTitleForMenuType(menu?.menuType),
      brandName: getAuthUser()?.customer?.name || "",
      copy: DEFAULT_PRINT_COPY_SK,
    };
  };

  const doMailchimpSend = async () => {
    setMcConfirmOpen(false);
    setMcSending(true);
    try {
      const res = await mailchimpSend(buildMailchimpPayload());
      if (res?.success) {
        enqueueSnackbar(
          t("pages.dsokDailyMenu.mcSent", {
            defaultValue: "Menu odoslané cez Mailchimp.",
          }),
          { variant: "success" },
        );
      } else {
        enqueueSnackbar(res?.reason || "Odoslanie zlyhalo.", {
          variant: "error",
        });
      }
    } catch (e) {
      const reason = e?.response?.data?.reason;
      const map = {
        not_configured: "Mailchimp nie je nakonfigurovaný (záložka Integrácie).",
        empty_menu: "Menu nemá žiadne položky.",
        no_from_name: "Chýba meno odosielateľa (Integrácie → Mailchimp).",
        from_name_is_email:
          "Odosielateľ (from name) nesmie byť e-mail — zadaj meno, napr. Catering Piešťany.",
        invalid_reply_to:
          "Chýba/neplatný reply-to email (Integrácie → Mailchimp).",
        send_failed: "Mailchimp odmietol odoslanie.",
      };
      enqueueSnackbar(
        e?.response?.data?.error ||
          map[reason] ||
          t("pages.dsokDailyMenu.mcSendError", {
            defaultValue: "Odoslanie cez Mailchimp zlyhalo.",
          }),
        { variant: "error" },
      );
    } finally {
      setMcSending(false);
    }
  };

  const doMailchimpTestEmail = async () => {
    const addr = String(mcTestEmailAddr || "").trim();
    if (!addr) return;
    setMcTestSending(true);
    try {
      const res = await mailchimpTestEmail({
        email: addr,
        ...buildMailchimpPayload(),
      });
      if (res?.success) {
        enqueueSnackbar(
          t("pages.dsokDailyMenu.mcTestSent", {
            defaultValue: `Testovací e-mail odoslaný na ${addr}.`,
          }),
          { variant: "success" },
        );
        setMcTestOpen(false);
      } else {
        enqueueSnackbar(res?.reason || "Test e-mail zlyhal.", {
          variant: "error",
        });
      }
    } catch (e) {
      const reason = e?.response?.data?.reason;
      const map = {
        not_configured: "Mailchimp nie je nakonfigurovaný (záložka Integrácie).",
        no_valid_email: "Neplatná e-mailová adresa.",
        no_from_name: "Chýba meno odosielateľa (Integrácie → Mailchimp).",
        from_name_is_email:
          "Odosielateľ (from name) nesmie byť e-mail — zadaj meno, napr. Catering Piešťany.",
        invalid_reply_to:
          "Chýba/neplatný reply-to email (Integrácie → Mailchimp).",
        test_failed: "Mailchimp odmietol testovací e-mail.",
      };
      enqueueSnackbar(
        e?.response?.data?.error ||
          map[reason] ||
          t("pages.dsokDailyMenu.mcTestError", {
            defaultValue: "Testovací e-mail zlyhal.",
          }),
        { variant: "error" },
      );
    } finally {
      setMcTestSending(false);
    }
  };

  if (!menu) {
    return null;
  }

  // Podklady pre PDF/tlač lístka (tlačový rám je vždy v DOM, skrytý mimo obrazovky).
  const printWeek = buildWeekFromMenu(menu, {
    lang,
    resolveProduct: (id) => {
      const p = menu?.productsById?.[id];
      return p ? enrichRowProduct({ ...p, id }) : null;
    },
  });
  const printBrandName = getAuthUser()?.customer?.name || "";
  const printSheetTitle = sheetTitleForMenuType(menu?.menuType);

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/daily-menu">
          {t("sidebar.menuItem.dsokDailyMenu", { defaultValue: "Daily Menu" })}
        </Link>
        <Typography color="text.primary">{getMenuDisplayName(menu)}</Typography>
      </Breadcrumbs>
      {saveError && (
        <Alert
          severity="error"
          onClose={() => setSaveError(null)}
          sx={{ mb: 2 }}
        >
          <Typography component="span" variant="body2" fontWeight={600}>
            {saveError.message}
          </Typography>
          {(saveError.code != null || saveError.status) && (
            <Typography
              component="span"
              variant="caption"
              display="block"
              sx={{ mt: 0.5, opacity: 0.9 }}
            >
              {saveError.code != null && `HTTP ${saveError.code}`}
              {saveError.code != null && saveError.status && " · "}
              {saveError.status && String(saveError.status)}
            </Typography>
          )}
        </Alert>
      )}
      <Div sx={{ display: "flex" }}>
        <JumboCardQuick
          title={
            <Typography
              component="div"
              sx={{
                display: "flex",
                alignItems: "center",
                [theme.breakpoints.down("md")]: { flexWrap: "wrap" },
              }}
            >
              <Div
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography
                  variant="h4"
                  mb={0}
                  sx={{
                    minWidth: 245,
                    [theme.breakpoints.down("md")]: {
                      minWidth: "100%",
                      marginBottom: 2,
                    },
                  }}
                >
                  {getMenuDisplayName(menu)}
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Link
                    to="/customer/dsok/daily-menu"
                    style={{ textDecoration: "none" }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FastRewindIcon />}
                    >
                      {t("buttons.back")}
                    </Button>
                  </Link>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={handleExportCsv}
                  >
                    {t("pages.dsokDailyMenu.exportCsv", {
                      defaultValue: "CSV",
                    })}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PictureAsPdfOutlinedIcon />}
                    onClick={triggerDailyMenuPrint}
                  >
                    {t("pages.dsokDailyMenu.exportPdf", {
                      defaultValue: "PDF",
                    })}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      docxExporting ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DescriptionOutlinedIcon />
                      )
                    }
                    onClick={handleExportDocx}
                    disabled={docxExporting}
                  >
                    {t("pages.dsokDailyMenu.exportDocx", {
                      defaultValue: "Word",
                    })}
                  </Button>
                  {mcEnabled && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="success"
                      startIcon={
                        mcSending ? (
                          <CircularProgress size={16} />
                        ) : (
                          <EmailOutlinedIcon />
                        )
                      }
                      onClick={() => setMcConfirmOpen(true)}
                      disabled={mcSending}
                    >
                      {t("pages.dsokDailyMenu.mcSend", {
                        defaultValue: "Odoslať cez Mailchimp",
                      })}
                    </Button>
                  )}
                  {mcEnabled && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setMcTestOpen(true)}
                    >
                      {t("pages.dsokDailyMenu.mcTestEmail", {
                        defaultValue: "Test e-mail",
                      })}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<BrowserUpdatedIcon />}
                    sx={{ boxShadow: "none" }}
                    size="small"
                    disabled={updating || !menu?.weekStartDate}
                    onClick={handleSave}
                  >
                    {t("buttons.update")}
                  </Button>
                </Stack>
              </Div>
            </Typography>
          }
          headerSx={{
            "& .MuiCardHeader-action": { my: -0.75 },
          }}
          sx={{ width: "100%" }}
          wrapperSx={{
            p: 0,
            "&:last-child": { pb: 2 },
          }}
        >
          <Stack spacing={2} sx={{ mb: 2, width: "100%", px: 2 }}>
            <Stack
              direction="row"
              spacing={2}
              sx={{ flexWrap: "wrap", rowGap: 2 }}
            >
              <Box sx={{ maxWidth: 220, minWidth: 200, flex: "0 0 auto" }}>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={sk}
                >
                  <DatePicker
                    label={t("pages.dsokDailyMenu.weekStartDate", {
                      defaultValue: "Dátum začiatku týždňa",
                    })}
                    value={weekStartDate}
                    onChange={handleWeekStartDateChange}
                    sx={{ width: "100%" }}
                    format="d.M.yyyy"
                    slotProps={{
                      textField: {
                        size: "small",
                        required: true,
                        error: !!weekStartDateError,
                        helperText: weekStartDateError,
                        InputLabelProps: { shrink: true },
                      },
                      actionBar: { actions: ["clear"] },
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Box sx={{ maxWidth: 220, minWidth: 200, flex: "0 0 auto" }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("pages.dsokDailyMenu.menuType", {
                    defaultValue: "Typ menu",
                  })}
                  value={menu?.menuType || ""}
                  onChange={(e) =>
                    setMenu((prev) => ({ ...prev, menuType: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                >
                  {availableMenuTypes.map((typ) => (
                    <MenuItem key={typ.code} value={typ.code}>
                      {typ.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>
            <Box
              sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}
            >
              <Tabs
                value={selectedDay}
                onChange={(_, val) => setSelectedDay(val)}
              >
                {DAYS.map((day) => {
                  const dateStr = datesForWeek[day]
                    ? format(parseISO(datesForWeek[day]), "d.M.", {
                        locale: sk,
                      })
                    : "";
                  const label = dateStr
                    ? `${t(`days.${day}`)} (${dateStr})`
                    : t(`days.${day}`);
                  return <Tab key={day} label={label} value={day} />;
                })}
              </Tabs>
            </Box>
          </Stack>
          <Stack sx={{ px: 2, pb: 2 }} spacing={2}>
            <Typography variant="h6">{t("pages.menus.soups")}</Typography>
            <FormControl sx={{ width: "100%", maxWidth: 400 }}>
              <Autocomplete
                key={soupAutocompleteKey}
                options={soupOptions}
                getOptionLabel={(opt) => getOptionDisplayName(opt, lang)}
                isOptionEqualToValue={(a, b) =>
                  (a?.id ?? a?._id) === (b?.id ?? b?._id)
                }
                filterOptions={(options, state) =>
                  (options || []).filter((opt) =>
                    String(getOptionDisplayName(opt, lang) || "")
                      .toLowerCase()
                      .includes(String(state.inputValue || "").toLowerCase()),
                  )
                }
                value={null}
                inputValue={soupAutocompleteInput}
                onInputChange={(_, value) =>
                  setSoupAutocompleteInput(value ?? "")
                }
                onChange={(_, option) => {
                  if (option) {
                    handleAddProduct(option, "soup");
                    setSoupAutocompleteInput("");
                    setSoupAutocompleteKey((k) => k + 1);
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option?.id ?? option?._id}>
                    {getOptionDisplayName(option, lang)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label={t("pages.menus.selectSoups")} />
                )}
              />
            </FormControl>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => {
                const { active, over } = event;
                if (over?.id != null && active.id !== over.id) {
                  handleReorderSoups(active.id, over.id);
                }
              }}
            >
              <SortableContext
                items={soupIds}
                strategy={verticalListSortingStrategy}
              >
                {soupsResolved.map((product, idx) => (
                  <SortableProductRow
                    key={product.id}
                    product={product}
                    number={idx + 1}
                    lang={lang}
                    onToggleActive={(checked) =>
                      handleToggleActive(product.id, checked)
                    }
                    onDelete={() => handleRemoveProduct(product.id, "soup")}
                    onUpdate={(field, value) =>
                      handleUpdateProduct(product.id, field, value)
                    }
                  />
                ))}
              </SortableContext>
            </DndContext>

            <Typography variant="h6" sx={{ mt: 2 }}>
              {t("pages.menus.meals")}
            </Typography>
            <FormControl sx={{ width: "100%", maxWidth: 400 }}>
              <Autocomplete
                key={mealAutocompleteKey}
                options={mealOptions}
                getOptionLabel={(opt) => getOptionDisplayName(opt, lang)}
                isOptionEqualToValue={(a, b) =>
                  (a?.id ?? a?._id) === (b?.id ?? b?._id)
                }
                filterOptions={(options, state) =>
                  (options || []).filter((opt) =>
                    String(getOptionDisplayName(opt, lang) || "")
                      .toLowerCase()
                      .includes(String(state.inputValue || "").toLowerCase()),
                  )
                }
                value={null}
                inputValue={mealAutocompleteInput}
                onInputChange={(_, value) =>
                  setMealAutocompleteInput(value ?? "")
                }
                onChange={(_, option) => {
                  if (option) {
                    handleAddProduct(option, "meal");
                    setMealAutocompleteInput("");
                    setMealAutocompleteKey((k) => k + 1);
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option?.id ?? option?._id}>
                    {getOptionDisplayName(option, lang)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label={t("pages.menus.selectMeals")} />
                )}
              />
            </FormControl>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => {
                const { active, over } = event;
                if (over?.id != null && active.id !== over.id) {
                  handleReorderMeals(active.id, over.id);
                }
              }}
            >
              <SortableContext
                items={mealIds}
                strategy={verticalListSortingStrategy}
              >
                {mealsResolved.map((product, idx) => (
                  <SortableProductRow
                    key={product.id}
                    product={product}
                    number={idx + 1}
                    lang={lang}
                    onToggleActive={(checked) =>
                      handleToggleActive(product.id, checked)
                    }
                    onDelete={() => handleRemoveProduct(product.id, "meal")}
                    onUpdate={(field, value) =>
                      handleUpdateProduct(product.id, field, value)
                    }
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Stack>
        </JumboCardQuick>
      </Div>
      <LunchMenuPrintFrame
        week={printWeek}
        brandName={printBrandName}
        sheetTitle={printSheetTitle}
        soupLabel={t("pages.dsokDailyMenu.soupLabel", {
          defaultValue: "Polievka",
        })}
        printCopy={DEFAULT_PRINT_COPY_SK}
        logoSrc={menuLogo}
      />

      {/* Potvrdenie reálneho odoslania cez Mailchimp */}
      <Dialog
        open={mcConfirmOpen}
        onClose={() => setMcConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Odoslať menu cez Mailchimp?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            E-mail sa NAOZAJ rozpošle všetkým odberateľom vo zvolenej audience.
          </Alert>
          <Typography variant="body2">
            Odošle sa aktuálne týždenné menu ako kampaň. Pred odoslaním odporúčam
            poslať si „Test e-mail".
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMcConfirmOpen(false)}>Zrušiť</Button>
          <Button variant="contained" color="success" onClick={doMailchimpSend}>
            Áno, odoslať
          </Button>
        </DialogActions>
      </Dialog>

      {/* Testovací e-mail cez Mailchimp na jednu adresu */}
      <Dialog
        open={mcTestOpen}
        onClose={() => setMcTestOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Testovací e-mail (Mailchimp)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Pošle sa testovacia verzia aktuálneho týždenného menu na zadanú
            adresu (reálnej audience sa netýka).
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="email"
            label="E-mailová adresa"
            value={mcTestEmailAddr}
            onChange={(e) => setMcTestEmailAddr(e.target.value)}
            placeholder="meno@firma.sk"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMcTestOpen(false)}>Zrušiť</Button>
          <Button
            variant="contained"
            onClick={doMailchimpTestEmail}
            disabled={mcTestSending || !mcTestEmailAddr.trim()}
            startIcon={mcTestSending ? <CircularProgress size={16} /> : null}
          >
            Odoslať test
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DsokDailyMenuEdit;
